import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  Select,
} from '@mui/material';
import { z } from 'zod';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import { apiPost, apiPut } from '../../utils/apiClient';
import {
  Subtype,
  TrialBooking,
  CreateUserRequest,
} from '../../types/TrialBookingTable.type';
import dayjs from 'dayjs';
import { useTranslation } from '../../i18n';

type TrialBookingInfoDialogProps = {
  open: boolean;
  handleClose: () => void;
  trialBooking: TrialBooking | null;
  fetchTrialBookingList: () => void;
  subtypeOptions: Subtype[];
};

const TrialBookingInfoInput = z.object({
  id: z.number(),
  firstName: z.string().min(1, 'Please enter first name'),
  lastName: z.string().min(1, 'Please enter last name'),
  email: z
    .string()
    .email('Please enter a valid email')
    .optional()
    .nullable()
    .or(z.literal('')),
  phone: z.string().min(1, 'Please enter phone number'),
  parent: z.string().optional(),
  dob: z
    .string({
      required_error: 'Please enter date of birth',
    })
    .date('Please enter a valid date'),
  subtypeId: z
    .number({
      required_error: 'Please select a program',
    })
    .min(1, 'Please select a program'),
  trialDate: z
    .string({
      required_error: 'Please select a trial date',
    })
    .datetime({ local: true, message: 'Please enter a valid date' }),
  note: z.string().optional(),
  result: z.string(),
});
type TrialBookingInfoInputType = z.infer<typeof TrialBookingInfoInput>;
type FormDataTrialBookingErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  parent?: string;
  dob?: string;
  subtypeId?: string;
  trialDate?: string;
  note?: string;
};

const TrialBookingInfoDialog = ({
  open,
  handleClose,
  trialBooking,
  fetchTrialBookingList,
  subtypeOptions,
}: TrialBookingInfoDialogProps) => {
  const { t } = useTranslation();
  const [formError, setFormError] = React.useState<FormDataTrialBookingErrors>(
    {},
  );
  const [result, setResult] = React.useState('');
  const [subtype, setSubtype] = React.useState('');

  const [enrollConfirm, setEnrollConfirm] = React.useState(false);

  const handleConfirm = async (data: TrialBookingInfoInputType) => {
    try {
      if (trialBooking) {
        const updateRes = await apiPut<
          {
            status: number;
            message: string;
            data: TrialBooking;
          },
          TrialBookingInfoInputType
        >('/trial-booking/update', data);
        notifyAPIClient(updateRes.message);
      }
      fetchTrialBookingList();
      handleClose();
    } catch (error) {
      notifyFailure();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = TrialBookingInfoInput.parse({
        id: trialBooking?.id,
        firstName: formData.get('firstName'),
        lastName: formData.get('lastName'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        parent: formData.get('parent'),
        dob: dayjs(formData.get('dob') as string).format('YYYY-MM-DD'),
        subtypeId: Number(subtype),
        trialDate: dayjs(formData.get('trialDate') as string).format(
          'YYYY-MM-DDTHH:mm:ss',
        ),
        note: formData.get('note'),
        result: result,
      });

      setFormError({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormDataTrialBookingErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormDataTrialBookingErrors;
          fieldErrors[field] = err.message;
        });
        setFormError(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient('An error occurred while submitting the form.');
      }
    }
  };

  const [pendingEnrollData, setPendingEnrollData] =
    React.useState<TrialBookingInfoInputType | null>(null);
  const handleEnrollConfirm = async (data: TrialBookingInfoInputType) => {
    try {
      const payload: CreateUserRequest = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        phone: data.phone,
        parent: data.parent,
        dob: data.dob,
      };

      await apiPost<null, CreateUserRequest>('/user/create', payload);

      handleConfirm(data);
    } catch (error) {
      notifyFailure();
    }
  };

  React.useEffect(() => {
    if (trialBooking) {
      setResult(trialBooking.result);
      setSubtype(trialBooking.subtype.id.toString());
      setFormError({});
    }
  }, [trialBooking]);

  return (
    <>
      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 'calc(100vh - 32px)',
          },
        }}
      >
        <form
          onSubmit={(event) => {
            if (result === 'ENROLL') {
              event.preventDefault();

              const formData = new FormData(event.currentTarget);
              try {
                const data = TrialBookingInfoInput.parse({
                  id: trialBooking?.id,
                  firstName: formData.get('firstName'),
                  lastName: formData.get('lastName'),
                  email: formData.get('email'),
                  phone: formData.get('phone'),
                  parent: formData.get('parent'),
                  dob: dayjs(formData.get('dob') as string).format(
                    'YYYY-MM-DD',
                  ),
                  subtypeId: Number(subtype),
                  trialDate: dayjs(formData.get('trialDate') as string).format(
                    'YYYY-MM-DDTHH:mm:ss',
                  ),
                  note: formData.get('note'),
                  result: result,
                });
                setPendingEnrollData(data);
                setFormError({});
                setEnrollConfirm(true);
              } catch (error) {
                if (error instanceof z.ZodError) {
                  const fieldErrors: FormDataTrialBookingErrors = {};
                  error.errors.forEach((err) => {
                    const field = err
                      .path[0] as keyof FormDataTrialBookingErrors;
                    fieldErrors[field] = err.message;
                  });
                  setFormError(fieldErrors);
                } else {
                  console.error(error);
                  notifyAPIClient(
                    'An error occurred while submitting the form.',
                  );
                }
              }
            } else {
              handleSubmit(event);
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <DialogTitle sx={{ flexShrink: 0 }}>
            {t('trialBooking.dialogTitle')}
          </DialogTitle>
          <DialogContent sx={{ minHeight: 0, overflowY: 'auto' }}>
            <TextField
              margin="dense"
              id="firstName"
              name="firstName"
              className="required-field"
              label={t('trialBooking.fieldFirstName')}
              type="text"
              fullWidth
              variant="standard"
              defaultValue={trialBooking?.firstName || ''}
              error={Boolean(formError.firstName)}
              helperText={formError.firstName}
            />

            <TextField
              margin="dense"
              id="lastName"
              name="lastName"
              className="required-field"
              label={t('trialBooking.fieldLastName')}
              type="text"
              fullWidth
              variant="standard"
              defaultValue={trialBooking?.lastName || ''}
              error={Boolean(formError.lastName)}
              helperText={formError.lastName}
            />

            <TextField
              margin="dense"
              id="email"
              name="email"
              label={t('trialBooking.fieldEmail')}
              type="text"
              fullWidth
              variant="standard"
              defaultValue={trialBooking?.email || ''}
              error={Boolean(formError.email)}
              helperText={formError.email}
            />

            <TextField
              margin="dense"
              id="phone"
              name="phone"
              className="required-field"
              label={t('trialBooking.fieldPhone')}
              type="text"
              fullWidth
              variant="standard"
              defaultValue={trialBooking?.phone || ''}
              error={Boolean(formError.phone)}
              helperText={formError.phone}
            />

            <TextField
              margin="dense"
              id="parent"
              name="parent"
              label={t('trialBooking.fieldParentName')}
              type="text"
              fullWidth
              variant="standard"
              defaultValue={trialBooking?.parent || ''}
              error={Boolean(formError.parent)}
              helperText={formError.parent}
            />

            <TextField
              margin="dense"
              id="dob"
              name="dob"
              className="required-field"
              label={t('trialBooking.fieldDateOfBirth')}
              type="date"
              fullWidth
              variant="standard"
              defaultValue={
                trialBooking?.dob
                  ? new Date(trialBooking.dob).toISOString().split('T')[0]
                  : ''
              }
              error={Boolean(formError.dob)}
              helperText={formError.dob}
            />

            <FormControl margin="dense" fullWidth variant="standard">
              <InputLabel id="program-label" className="required-field">
                {t('trialBooking.fieldProgram')}
              </InputLabel>
              <Select
                labelId="program-label"
                id="program"
                value={subtype}
                onChange={(event) => setSubtype(event.target.value as string)}
                label={t('trialBooking.fieldProgram')}
              >
                {subtypeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {formError.subtypeId && (
                <FormHelperText>{formError.subtypeId}</FormHelperText>
              )}
            </FormControl>

            <TextField
              margin="dense"
              id="trialDate"
              name="trialDate"
              className="required-field"
              label={t('trialBooking.fieldTrialDate')}
              type="datetime-local"
              fullWidth
              variant="standard"
              defaultValue={
                trialBooking?.trialDate
                  ? dayjs(trialBooking.trialDate).format('YYYY-MM-DDTHH:mm:ss')
                  : ''
              }
              error={Boolean(formError.trialDate)}
              helperText={formError.trialDate}
            />

            <FormControl margin="dense" fullWidth variant="standard">
              <InputLabel id="result-label">
                {t('trialBooking.fieldResult')}
              </InputLabel>
              <Select
                id="result"
                name="result"
                labelId="result-label"
                value={result}
                onChange={(event) => setResult(event.target.value as string)}
                label={t('trialBooking.fieldResult')}
              >
                <MenuItem value="PENDING">
                  {t('trialBooking.resultPending')}
                </MenuItem>
                <MenuItem value="ENROLL">
                  {t('trialBooking.resultEnroll')}
                </MenuItem>
                <MenuItem value="NOT_COME">
                  {t('trialBooking.resultNotCome')}
                </MenuItem>
                <MenuItem value="CONSIDER">
                  {t('trialBooking.resultConsider')}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="normal"
              id="note"
              name="note"
              label={t('trialBooking.fieldNote')}
              type="text"
              fullWidth
              defaultValue={trialBooking?.note || ''}
              multiline
              minRows={3}
              error={Boolean(formError.note)}
              helperText={formError.note}
            />
          </DialogContent>
          <DialogActions sx={{ flexShrink: 0 }}>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="submit">{t('trialBooking.confirm')}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        fullWidth
        open={enrollConfirm}
        onClose={() => setEnrollConfirm(false)}
      >
        <DialogTitle>{t('trialBooking.enrollConfirmTitle')}</DialogTitle>
        <DialogContent>
          <p>{t('trialBooking.enrollConfirmBody')}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollConfirm(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (pendingEnrollData) {
                handleEnrollConfirm(pendingEnrollData);
                setEnrollConfirm(false);
                setPendingEnrollData(null);
              }
            }}
          >
            {t('trialBooking.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrialBookingInfoDialog;
